// Copyright (c) 2021-22, HelmCyber, Inc.
'use strict'

import axios from 'axios'
import PropTypes from 'prop-types'
import { useContext, useRef, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { withTranslation } from 'react-i18next'
import { withTheme } from 'styled-components'

import { UiContext } from 'js/contexts/uiContext'

const ChangePasswordModal = ({ onClose, isCancelable, t, theme }) => {
    const [ui] = useContext(UiContext)

    const [currentPasswordError, setCurrentPasswordError] = useState(null)
    const [newPasswordError, setNewPasswordError] = useState(null)
    const [confirmNewPasswordError, setConfirmNewPasswordError] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)

    const currentPasswordInput = useRef()
    const confirmNewPasswordInput = useRef()
    const newPasswordInput = useRef()

    const confirm = async () => {
        try {
            const currentPassword = currentPasswordInput.current.value.trim()
            const newPassword = newPasswordInput.current.value.trim()
            const confirmNewPassword = confirmNewPasswordInput.current.value.trim()
            let valid = true

            setCurrentPasswordError(null)
            setNewPasswordError(null)
            setConfirmNewPasswordError(null)
            setErrorMessage(null)

            if (currentPassword.length === 0) {
                setCurrentPasswordError('Required')
                valid = false
            }

            if (newPassword.length === 0) {
                setNewPasswordError('Required')
                valid = false
            }

            if (confirmNewPassword.length === 0) {
                setConfirmNewPasswordError('Required')
                valid = false
            }

            if (newPassword !== confirmNewPassword) {
                setConfirmNewPasswordError(t('change_password_modal.confirm_password_error_label'))
                valid = false
            }

            if (!valid) return

            await axios.put(
                `/users/${ui.user?.uid}/password`,
                {
                    username: ui.user?.username,
                    currentPassword,
                    newPassword,
                },
                { noAuthFailure: true }
            )

            submit(newPassword)
        } catch (err) {
            switch (err.response.status) {
                case 400:
                    setErrorMessage('Form contains invalid values')
                    break
                case 401:
                    setCurrentPasswordError('Current password is not correct')
                    break
                case 404:
                    setErrorMessage(t('change_password_modal.user_does_not_exist_label'))
                    break
                case 409:
                    setErrorMessage(t('change_password_modal.password_already_taken_label'))
                    break
                default:
                    setErrorMessage(t('change_password_modal.password_change_failed_label'))
                    break
            }
        }
    }

    const cancel = () => {
        setCurrentPasswordError(null)
        setNewPasswordError(null)
        setConfirmNewPasswordError(null)
        setErrorMessage(null)

        if (onClose) onClose()
    }

    const submit = newPassword => {
        setCurrentPasswordError(null)
        setNewPasswordError(null)
        setConfirmNewPasswordError(null)
        setErrorMessage(null)

        if (onClose) onClose(newPassword)
    }

    return (
        <Modal show={true} onHide={cancel} keyboard={isCancelable} backdrop={isCancelable ? true : 'static'}>
            <Modal.Header closeButton={isCancelable} closeVariant={theme.colors.modal.closeVariant}>
                <Modal.Title>{t('navigation.change_password_title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form noValidate onSubmit={confirm}>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="currentPassword">{t('change_password_modal.current_password_label')}</Form.Label>
                        <Form.Control
                            id="currentPassword"
                            type="password"
                            autoComplete="off"
                            autoFocus={true}
                            ref={currentPasswordInput}
                            isInvalid={currentPasswordError}
                        />
                        <Form.Control.Feedback type="invalid">{currentPasswordError}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="newPassword">{t('change_password_modal.new_password_label')}</Form.Label>
                        <Form.Control id="newPassword" type="password" autoComplete="off" ref={newPasswordInput} isInvalid={newPasswordError} />
                        <Form.Control.Feedback type="invalid">{newPasswordError}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="confirmNewPassword">
                        <Form.Label htmlFor="confirmNewPassword">{t('change_password_modal.confirm_password_label')}</Form.Label>
                        <Form.Control
                            id="confirmNewPassword"
                            type="password"
                            autoComplete="off"
                            ref={confirmNewPasswordInput}
                            isInvalid={confirmNewPasswordError}
                        />
                        <Form.Control.Feedback type="invalid">{confirmNewPasswordError}</Form.Control.Feedback>
                    </Form.Group>
                    {errorMessage && <span className="text-danger">{errorMessage}</span>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {isCancelable && (
                    <Button variant="secondary" onClick={cancel}>
                        {t('common.cancel_button')}
                    </Button>
                )}
                <Button className="ms-2" onClick={confirm}>
                    {t('common.confirm_button')}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

ChangePasswordModal.propTypes = {
    isCancelable: PropTypes.bool,
    onClose: PropTypes.func,
    t: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
}

ChangePasswordModal.defaultProps = {
    isCancelable: true,
}

export default withTranslation()(withTheme(ChangePasswordModal))
